import { v4 as uuidv4 } from "uuid";
import { getPhotoDuplicatePairs, normalizePastedProductServicePhotos } from "./productServicePhotos";

const withNormalizedPhotos = (ps) => normalizePastedProductServicePhotos(ps);

function generateNewIds(ps, newDocumentItemId) {
  const newPsId = uuidv4();
  let childrenIdReference = {};
  let newPs = {};
  if (ps?.is_bundle) {
    const sortedChildren = [...(ps?.children || [])].sort(
      (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)
    );
    childrenIdReference = sortedChildren.reduce((acc, current) => {
      acc[current.document_item_product_service_id] = uuidv4();
      return acc;
    }, {});

    newPs = withNormalizedPhotos({
      ...ps,
      document_item_product_service_id: newPsId,
      document_item_id: newDocumentItemId,
      product_service_id: uuidv4(),
      is_inside_bundle: false,
      product_service_parent_id: [],
      bundle_group: {
        items: ps?.bundle_group?.items.map((id) => childrenIdReference[id]) || [],
        default_group_ps_item_id: childrenIdReference[ps?.bundle_group?.default_group_ps_item_id] || ""
      },
      children: sortedChildren.map((item, index) => {
        return withNormalizedPhotos({
          ...item,
          document_item_product_service_id: childrenIdReference[item.document_item_product_service_id],
          product_service_parent_id: [newPsId],
          product_service_id: uuidv4(),
          document_item_id: newDocumentItemId,
          is_inside_bundle: true,
          is_bundle: false,
          position: index + 1
        });
      })
    });
    return {
      newPs,
      childrenIdReference,
      oldPs: ps
    };
  }
  // Pasted bundle children become standalone top-level rows (matches bulk edit paste).
  if (ps.is_inside_bundle) {
    newPs = withNormalizedPhotos({
      ...ps,
      document_item_product_service_id: newPsId,
      document_item_id: newDocumentItemId,
      product_service_id: uuidv4(),
      is_inside_bundle: false,
      is_bundle: false,
      product_service_parent_id: [],
      children: [],
      total: Number(ps.sell || 0) * Number(ps.quantity || 1) - Number(ps.discount || 0)
    });
    return {
      newPs,
      childrenIdReference,
      oldPs: ps
    };
  }
  newPs = withNormalizedPhotos({
    ...ps,
    document_item_product_service_id: newPsId,
    product_service_id: uuidv4(),
    document_item_id: newDocumentItemId,
    is_inside_bundle: false,
    product_service_parent_id: []
  });

  return {
    newPs,
    childrenIdReference,
    oldPs: ps
  };
}

// Combined "Short/Long Description" column: shows the short description with the long
// description stacked on the line beneath it. Available as a toggleable column alongside the
// plain "Description" column.
export const SHORT_LONG_DESCRIPTION_COLUMN = {
  position: 2.5,
  visible: false,
  name: "short_long_description",
  header: "Description",
  defaultFlex: 1,
  minWidth: 70,
  defaultWidth: 210.93
};

// Ensure a product/service column config contains the Short/Long Description column. Existing
// documents persisted their column list before this column existed, so inject it (hidden) right
// after the short_description column whenever it's missing. Idempotent.
export const ensureShortLongDescriptionColumn = (tableData) => {
  if (!Array.isArray(tableData)) return tableData;
  if (tableData.some((col) => col?.name === "short_long_description")) return tableData;
  const insertAfter = tableData.findIndex((col) => col?.name === "short_description");
  const column = { ...SHORT_LONG_DESCRIPTION_COLUMN };
  if (insertAfter === -1) return [...tableData, column];
  const next = [...tableData];
  next.splice(insertAfter + 1, 0, column);
  return next;
};

export const adjustWidthsProportionally = (items, deduct = true, changedWidth) => {
  const total = items.reduce((sum, item) => sum + Number(item.defaultWidth), 0);

  if (total === 0) return items.map((item) => ({ ...item, width: Number(item.defaultWidth) }));

  return items.map((item) => {
    const proportion = Number(item.defaultWidth) / total;
    const adjustment = proportion * changedWidth;

    return {
      ...item,
      defaultWidth: deduct ? Number(item.defaultWidth) - adjustment : Number(item.defaultWidth) + adjustment
    };
  });
};

const pushPhotoDuplicatePairs = (acc, oldPs, newPs, generateProductServiceUrl, tenantId) => {
  if (tenantId) {
    getPhotoDuplicatePairs(oldPs, newPs, tenantId).forEach((pair) => acc.push(pair));
    return acc;
  }

  const from = generateProductServiceUrl(oldPs);
  if (from) {
    acc.push({
      from,
      to: generateProductServiceUrl(newPs)
    });
  }

  return acc;
};

export function getDuplicateProductServicesMedia(productServices, generateProductServiceUrl, tenantId) {
  return productServices
    .reduce((acc, current) => {
      if (current?.oldPs?.is_bundle && current?.oldPs?.children?.length) {
        current.oldPs.children.forEach((oldChild) => {
          const newChild = current.newPs.children.find(
            (child) =>
              current.childrenIdReference[oldChild.document_item_product_service_id] ===
              child.document_item_product_service_id
          );
          if (newChild) {
            pushPhotoDuplicatePairs(acc, oldChild, newChild, generateProductServiceUrl, tenantId);
          }
        });
      }

      pushPhotoDuplicatePairs(acc, current.oldPs, current.newPs, generateProductServiceUrl, tenantId);
      return acc;
    }, [])
    .filter((psWithPhoto) => Boolean(psWithPhoto?.from));
}

export function getMaxProductServicePosition(items = []) {
  if (!items?.length) return 0;
  return Math.max(0, ...items.map((item) => Number(item?.position) || 0));
}

export function getNextTopLevelProductServicePosition(productServices = [], documentItemId) {
  const topLevel = productServices.filter(
    (ps) =>
      !ps?.is_inside_bundle &&
      (documentItemId == null || String(ps.document_item_id) === String(documentItemId))
  );
  return getMaxProductServicePosition(topLevel) + 1;
}

export function getNextBundleChildPosition(parent) {
  return getMaxProductServicePosition(parent?.children) + 1;
}

/** Normalize nested bundle children for document storage (1-based positions + flags). */
export function normalizeBundleChildrenForDocument(children = [], parentId) {
  return (children || []).map((child, index) => ({
    ...child,
    is_inside_bundle: true,
    is_bundle: false,
    product_service_parent_id: parentId ? [parentId] : child.product_service_parent_id || [],
    children: [],
    position: index + 1
  }));
}

/**
 * Append a newly created product/service onto the nested list (or nest into a bundle).
 * Shared by layout tile, bulk-edit dialog add, and properties settings so
 * external / internal / custom / bundle creates all use the same hierarchy rules.
 */
export function appendProductServiceToList(productServices = [], newProductService, documentItemId) {
  const parentId =
    newProductService?.product_service_parent_id?.[0] ||
    (newProductService?.add_to_bundle && newProductService.add_to_bundle !== "none"
      ? newProductService.add_to_bundle
      : null);

  if (parentId) {
    const parent = productServices.find((ps) => ps.document_item_product_service_id === parentId);
    const child = {
      ...newProductService,
      is_inside_bundle: true,
      is_bundle: false,
      product_service_parent_id: [parentId],
      children: [],
      position:
        Number(newProductService.position) > 0
          ? Number(newProductService.position)
          : getNextBundleChildPosition(parent)
    };
    return productServices.map((ps) => {
      if (ps.document_item_product_service_id !== parentId) return ps;
      return {
        ...ps,
        is_bundle: true,
        children: [...(ps.children || []), child]
      };
    });
  }

  const position =
    Number(newProductService?.position) > 0
      ? Number(newProductService.position)
      : getNextTopLevelProductServicePosition(productServices, documentItemId);

  const parentDocId = newProductService?.document_item_product_service_id;
  const hasChildren = Boolean(newProductService?.children?.length);
  const topLevel = {
    ...newProductService,
    is_inside_bundle: false,
    product_service_parent_id: [],
    position,
    is_bundle: Boolean(newProductService?.is_bundle || hasChildren),
    ...(hasChildren && parentDocId
      ? { children: normalizeBundleChildrenForDocument(newProductService.children, parentDocId) }
      : hasChildren
        ? { children: normalizeBundleChildrenForDocument(newProductService.children, null) }
        : {})
  };

  return [...productServices, topLevel];
}

/** Re-number nested bundle children to contiguous 1-based positions (layout + bulk sync). */
export function renumberBundleChildren(children = []) {
  return (children || []).map((child, index) => ({ ...child, position: index + 1 }));
}

export function findBundleParentId(productServices = [], childId) {
  if (!childId) return null;
  const parent = (productServices || []).find((ps) =>
    ps?.children?.some((child) => child.document_item_product_service_id === childId)
  );
  return parent?.document_item_product_service_id || null;
}

function pruneBundleGroup(bundleGroup, removedId) {
  if (!bundleGroup) return bundleGroup;
  return {
    ...bundleGroup,
    items: (bundleGroup.items || []).filter((id) => id !== removedId),
    default_group_ps_item_id:
      bundleGroup.default_group_ps_item_id === removedId ? "" : bundleGroup.default_group_ps_item_id || ""
  };
}

function withPhotoFields(source, fallback = {}) {
  return {
    photos: source.photos ?? source.Photos ?? fallback.photos ?? fallback.Photos,
    photo: source.photo ?? source.Photo ?? fallback.photo ?? fallback.Photo ?? null
  };
}

/**
 * Apply an edit-modal save onto the nested product/service list.
 * Owns bundle membership + 1-based position assignment so layout, drag reorder,
 * and bulk-edit saves stay on the same scheme.
 */
export function applyProductServiceEdit(productServices = [], editedItem, documentItemId) {
  const itemId = editedItem?.document_item_product_service_id;
  if (!itemId) return productServices;

  const addToBundle = editedItem.add_to_bundle;
  const wantsBundle = Boolean(addToBundle && addToBundle !== "none");
  const currentParentId = findBundleParentId(productServices, itemId);
  const isCurrentlyNested = Boolean(currentParentId);

  const toStandalone = (item, position) => ({
    ...item,
    is_inside_bundle: false,
    is_bundle: item.is_bundle && (item.children?.length > 0),
    product_service_parent_id: [],
    ...(item.is_bundle && item.children?.length ? {} : { children: [] }),
    position
  });

  const toBundleChild = (item, parentId, position, fallback = {}) => ({
    ...item,
    ...withPhotoFields(item, fallback),
    is_inside_bundle: true,
    is_bundle: false,
    product_service_parent_id: [parentId],
    children: [],
    position
  });

  // Unbundle: leave parent, become top-level with next top-level position
  if (isCurrentlyNested && !wantsBundle) {
    const nextPos = getNextTopLevelProductServicePosition(productServices, documentItemId);
    return productServices.flatMap((ps) => {
      if (ps.document_item_product_service_id !== currentParentId) return [ps];
      const newChildren = renumberBundleChildren(
        (ps.children || []).filter((child) => child.document_item_product_service_id !== itemId)
      );
      return [
        {
          ...ps,
          bundle_group: pruneBundleGroup(ps.bundle_group, itemId),
          children: newChildren,
          is_bundle: newChildren.length > 0
        },
        toStandalone(editedItem, nextPos)
      ];
    });
  }

  // Update child inside the same bundle — keep existing within-bundle position
  if (isCurrentlyNested && wantsBundle && addToBundle === currentParentId) {
    return productServices.map((ps) => {
      if (ps.document_item_product_service_id !== currentParentId) return ps;
      return {
        ...ps,
        is_bundle: true,
        children: (ps.children || []).map((child) => {
          if (child.document_item_product_service_id !== itemId) return child;
          return toBundleChild(editedItem, currentParentId, child.position, child);
        })
      };
    });
  }

  // Move child from one bundle to another
  if (isCurrentlyNested && wantsBundle && addToBundle !== currentParentId) {
    return productServices.reduce((acc, ps) => {
      if (ps.document_item_product_service_id === currentParentId) {
        const newChildren = renumberBundleChildren(
          (ps.children || []).filter((child) => child.document_item_product_service_id !== itemId)
        );
        acc.push({
          ...ps,
          bundle_group: pruneBundleGroup(ps.bundle_group, itemId),
          children: newChildren,
          is_bundle: newChildren.length > 0
        });
        return acc;
      }
      if (ps.document_item_product_service_id === addToBundle) {
        acc.push({
          ...ps,
          is_bundle: true,
          children: [
            ...(ps.children || []),
            toBundleChild(editedItem, addToBundle, getNextBundleChildPosition(ps))
          ]
        });
        return acc;
      }
      if (ps.document_item_product_service_id === itemId) return acc;
      acc.push(ps);
      return acc;
    }, []);
  }

  // Nest an existing top-level item into a bundle
  if (!isCurrentlyNested && wantsBundle) {
    return productServices.reduce((acc, ps) => {
      if (ps.document_item_product_service_id === itemId) return acc;
      if (ps.document_item_product_service_id === addToBundle) {
        acc.push({
          ...ps,
          is_bundle: true,
          children: [
            ...(ps.children || []),
            toBundleChild(editedItem, addToBundle, getNextBundleChildPosition(ps))
          ]
        });
        return acc;
      }
      acc.push(ps);
      return acc;
    }, []);
  }

  // Normal top-level update — preserve existing top-level position
  return productServices.map((ps) => {
    if (ps.document_item_product_service_id !== itemId) return ps;
    const position =
      Number(editedItem.position) > 0 ? Number(editedItem.position) : Number(ps.position) || 1;
    return {
      ...toStandalone(editedItem, position),
      children: editedItem.children ?? ps.children,
      is_bundle: Boolean(editedItem.is_bundle || (editedItem.children || ps.children)?.length)
    };
  });
}

/** defaultPS uses position 0; treat non-positive values as unset. */
export function resolveProductServicePosition(explicitPosition, fallbackPosition) {
  const n = Number(explicitPosition);
  if (Number.isFinite(n) && n > 0) return n;
  return fallbackPosition;
}

export const DOCUMENT_ORIGIN_ID = {
  LEGACY: 0,
  INTERNAL: 1,
  EXTERNAL: 2,
  CUSTOM: 3
};

/**
 * Resolves document_origin_id for a PS item at creation/save time.
 * - Edit of existing item: preserves the current origin
 * - New from internal search: 1
 * - New from external product feed: 2
 * - New custom item (no search, bulk insert, PDF): 3
 */
export function resolveDocumentOriginId({ isNew, isCreatingFromSearch, isInternalSearch, existingOriginId }) {
  if (!isNew && existingOriginId != null) return existingOriginId;
  if (isCreatingFromSearch) return isInternalSearch ? DOCUMENT_ORIGIN_ID.INTERNAL : DOCUMENT_ORIGIN_ID.EXTERNAL;
  return DOCUMENT_ORIGIN_ID.CUSTOM;
}

export function shouldNotifyIntegrationOnProductAdd(documentOriginId) {
  return Number(documentOriginId) > 1;
}

export function buildIntegrationProductAddMessage(integrationName) {
  return `Products/Services may be added to ${integrationName}. Please make sure all information is correct.`;
}

export const defaultPS = {
  category: "",
  category_id: null,
  cost: 0,
  currency_id: 840,
  discount: 0,
  document_item_id: null,
  document_item_product_service_id: null,
  document_item_product_service_parent_id: null,
  document_origin_id: DOCUMENT_ORIGIN_ID.LEGACY,
  include_in_adjustment: true,
  location: null,
  long_description: null,
  manual: null,
  manufacturer: null,
  manufacturer_id: null,
  manufacturer_part_number: null,
  map: "",
  msrp: "",
  optional: false,
  optional_is_selected: false,
  photo: null,
  position: 0,
  quantity: "",
  quantity_adjustment_allowed: false,
  recurrence_id: null,
  recurrence_include_in_total: true,
  recurrence_option_id: 1,
  sell: 0,
  short_description: "",
  specification_sheet: null,
  sub_category: null,
  sub_category_id: null,
  taxable: true,
  tenant_id: null,
  tenant_part_number: null,
  total: 0,
  type: "",
  type_id: null,
  uom: "",
  uom_id: null,
  vendor_id: null,
  vendor_name: null
};

export default generateNewIds;
